import React, { useState } from 'react';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface StudentProfile {
  id?: string;
  name?: string;
  rollNumber?: string;
  class?: string;
  className?: string;
  section?: string;
  enrollmentId?: string;
  aadhaarNumber?: string;
  dateOfBirth?: string;
  email?: string;
  phone?: string;
  address?: string;
  parentName?: string;
  parentPhone?: string;
  parentEmail?: string;
  bloodGroup?: string;
  gender?: string;
  house?: string;
  transportAvailed?: string;
}

interface StudentProfileProps {
  domain: string;
}

const StudentProfile: React.FC<StudentProfileProps> = ({ domain }) => {
  const [studentId, setStudentId] = useState('');
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [originalProfile, setOriginalProfile] = useState<StudentProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadProfile = async () => {
    if (!studentId.trim()) {
      setError('Please enter a student ID or enrollment ID');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const searchId = studentId.trim();

      // Try 1: Direct document lookup by student ID
      const studentRef = doc(db, `school-users/${domain}/students`, searchId);
      const studentDoc = await getDoc(studentRef);

      if (studentDoc.exists()) {
        const loadedProfile = { id: studentDoc.id, ...studentDoc.data() } as StudentProfile;
        setProfile(loadedProfile);
        setOriginalProfile({ ...loadedProfile });
        setEditing(false);
        setError('');
        setStudentId(studentDoc.id); // Update with actual document ID
        return;
      }

      // Try 2: Search by enrollment ID
      const studentsRef = collection(db, `school-users/${domain}/students`);
      const enrollmentQuery = query(studentsRef, where('enrollmentId', '==', searchId));
      const enrollmentSnapshot = await getDocs(enrollmentQuery);

      if (!enrollmentSnapshot.empty) {
        const foundDoc = enrollmentSnapshot.docs[0];
        const loadedProfile = { id: foundDoc.id, ...foundDoc.data() } as StudentProfile;
        setProfile(loadedProfile);
        setOriginalProfile({ ...loadedProfile });
        setEditing(false);
        setError('');
        setStudentId(foundDoc.id); // Update with actual document ID
        return;
      }

      // Not found by either method
      setError('Student not found. Please check the student ID or enrollment ID.');
      setProfile(null);
      setOriginalProfile(null);
    } catch (error: any) {
      console.error('Error loading profile:', error);
      setError(`Failed to load profile: ${error.message || 'Unknown error'}`);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profile) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const studentRef = doc(db, `school-users/${domain}/students`, studentId.trim());
      const { id, ...updateData } = profile;
      await updateDoc(studentRef, updateData);
      setOriginalProfile(profile); // Update original profile after successful save
      setSuccess('Profile updated successfully!');
      setEditing(false);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setError(`Failed to update profile: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-person-vcard me-2 text-primary-custom"></i>
          Update Student Profile
        </h5>
      </div>
      <div className="card-body">
        <div className="row mb-4">
          <div className="col-md-8">
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Student ID or Enrollment ID (e.g., student_10_A_00001 or 25PVSS00001)"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && loadProfile()}
              />
              <button className="btn btn-primary" onClick={loadProfile} disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="bi bi-search me-2"></i>
                    Load Profile
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {success && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <i className="bi bi-check-circle me-2"></i>
            {success}
            <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
          </div>
        )}

        {profile && (
          <form onSubmit={handleUpdate}>
            <h6 className="mb-3 text-primary-custom">
              <i className="bi bi-person me-2"></i>
              Basic Information
            </h6>
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={profile.name || ''}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  disabled={!editing}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Enrollment ID</label>
                <input
                  type="text"
                  className="form-control"
                  value={profile.enrollmentId || ''}
                  onChange={(e) => setProfile({ ...profile, enrollmentId: e.target.value })}
                  disabled={!editing}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Aadhaar Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={profile.aadhaarNumber || ''}
                  onChange={(e) => setProfile({ ...profile, aadhaarNumber: e.target.value })}
                  disabled={!editing}
                  maxLength={12}
                  placeholder="XXXX-XXXX-XXXX"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Class *</label>
                <input
                  type="text"
                  className="form-control"
                  value={profile.class || ''}
                  onChange={(e) => setProfile({ ...profile, class: e.target.value })}
                  disabled={!editing}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Section *</label>
                <input
                  type="text"
                  className="form-control"
                  value={profile.section || ''}
                  onChange={(e) => setProfile({ ...profile, section: e.target.value })}
                  disabled={!editing}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Class Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={profile.className || ''}
                  onChange={(e) => setProfile({ ...profile, className: e.target.value })}
                  disabled={!editing}
                  placeholder="e.g., Class 10B"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Roll Number *</label>
                <input
                  type="text"
                  className="form-control"
                  value={profile.rollNumber || ''}
                  onChange={(e) => setProfile({ ...profile, rollNumber: e.target.value })}
                  disabled={!editing}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Date of Birth</label>
                <input
                  type="date"
                  className="form-control"
                  value={profile.dateOfBirth || ''}
                  onChange={(e) => setProfile({ ...profile, dateOfBirth: e.target.value })}
                  disabled={!editing}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Gender</label>
                <select
                  className="form-select"
                  value={profile.gender || ''}
                  onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
                  disabled={!editing}
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Blood Group</label>
                <select
                  className="form-select"
                  value={profile.bloodGroup || ''}
                  onChange={(e) => setProfile({ ...profile, bloodGroup: e.target.value })}
                  disabled={!editing}
                >
                  <option value="">Select</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">House</label>
                <select
                  className="form-select"
                  value={profile.house || ''}
                  onChange={(e) => setProfile({ ...profile, house: e.target.value })}
                  disabled={!editing}
                >
                  <option value="">Select</option>
                  <option value="Red">Red</option>
                  <option value="Blue">Blue</option>
                  <option value="Green">Green</option>
                  <option value="Yellow">Yellow</option>
                </select>
              </div>
            </div>

            <h6 className="mb-3 text-primary-custom">
              <i className="bi bi-telephone me-2"></i>
              Contact Information
            </h6>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={profile.email || ''}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  disabled={!editing}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Phone</label>
                <input
                  type="tel"
                  className="form-control"
                  value={profile.phone || ''}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  disabled={!editing}
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">Address</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={profile.address || ''}
                  onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                  disabled={!editing}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Transport Availed</label>
                <select
                  className="form-select"
                  value={profile.transportAvailed || 'N'}
                  onChange={(e) => setProfile({ ...profile, transportAvailed: e.target.value })}
                  disabled={!editing}
                >
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                </select>
              </div>
            </div>

            <h6 className="mb-3 text-primary-custom">
              <i className="bi bi-people me-2"></i>
              Parent/Guardian Information
            </h6>
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Parent Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={profile.parentName || ''}
                  onChange={(e) => setProfile({ ...profile, parentName: e.target.value })}
                  disabled={!editing}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Parent Phone</label>
                <input
                  type="tel"
                  className="form-control"
                  value={profile.parentPhone || ''}
                  onChange={(e) => setProfile({ ...profile, parentPhone: e.target.value })}
                  disabled={!editing}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Parent Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={profile.parentEmail || ''}
                  onChange={(e) => setProfile({ ...profile, parentEmail: e.target.value })}
                  disabled={!editing}
                />
              </div>
            </div>

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setEditing(true)}
                disabled={editing || loading}
              >
                <i className="bi bi-pencil me-2"></i>Edit Profile
              </button>

              <button
                type="submit"
                className="btn btn-success"
                disabled={!editing || loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-2"></i>Save Changes
                  </>
                )}
              </button>

              {editing && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditing(false);
                    setProfile(originalProfile ? { ...originalProfile } : null);
                    setError('');
                    setSuccess('');
                  }}
                  disabled={loading}
                >
                  <i className="bi bi-x-lg me-2"></i>Cancel
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default StudentProfile;